import React, { FunctionComponent, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { RefactorActionInfo } from 'typescript'
import { AutoImage } from '../components/auto-image'
import { BannerImage } from '../components/banner-image'
import { BlogItemDetail } from '../components/blog-item-detail'
import { ContentContainer } from '../components/content-container'
import { HeadBannar } from '../components/head-bannar'
import { PostPaginator } from '../components/post-paginator'
import { SideBar } from '../components/side-bar'
import { useCategoriesQuery, useNewsPostsQuery, usePostsQuery } from '../generated/graphql'
import SvgLogo from '../generated/svg/logo'
import SvgRightTriangle from '../generated/svg/right-triangle'
import { isNotNullish } from '../utils/type-check'
import { getYearMonth, getMonthRange, isInRange, countByYearMonth, toYearMonth, Inner, MainContainer, PaginatorContainer, SideBarContainer, PostListItem, ArrowContainer } from './news-list-page'

const Outer = styled.div``

const ItemContainer = styled.div`
`

const ItemInner = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  @media (max-width: 60rem){
    align-items: flex-start;
    gap: 1rem;
  }
  ${PostListItem}:hover & {
    color: #aaa;
  }
`

const ThumbnailOuter = styled.div`
  flex: 0 0 12rem;
  @media (max-width: 60rem){
    flex: 0 0 10rem;
  }
  @media (max-width: 40rem){
    flex: 0 0 8rem;
  }
`
const ThumbnailProxy = styled.div`
  background-color: #fff;
  width: 100%;
  aspect-ratio: 3 / 2;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 40rem){
    aspect-ratio: 2.35 / 1;
  }
  svg {
    width: 60%;
    height: auto;
    display: block;
  }
`

const Thumbnail = styled.img`
  display: block;
  object-fit: cover;
  min-height: 0;
  width: 100%;
  height: auto;
  aspect-ratio: 3 / 2;
` 

const postInPage = 5

type BlogListPageProps = {

}
export const BlogListPage: FunctionComponent<BlogListPageProps> = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const targetMonth = getYearMonth(params.get('d'))
  const targetRange = targetMonth ? getMonthRange(targetMonth) : null
  const currentPage = Math.max(0, Number(params.get('p'))) || 1
  const targetCategory = params.get('c')

  const postsQuery = usePostsQuery({variables: {
    first: 100000
  }})

  const categoriesQuery = useCategoriesQuery()

  const filteredPosts = postsQuery.data?.posts?.nodes?.filter(isNotNullish).filter(post => {
    let pass = true
    if(targetRange &&!isInRange(new Date(post?.date ?? NaN).getTime(), ...targetRange)) pass = false
    if(targetCategory && post.categories?.nodes?.[0]?.slug !== targetCategory) pass = false
    return pass
  })

  const pagedPosts = filteredPosts?.slice(postInPage * (currentPage - 1), postInPage * currentPage)

  return <Outer>
    <HeadBannar desktop={() => <BannerImage src="/images/header_10.jpg" />} mobile={() => <BannerImage src="/images/header_10_mobile.jpg" />}></HeadBannar>
    <ContentContainer>
      <Inner>
        <SideBarContainer>
          <SideBar
            categoryMap={new Map([
              ...categoriesQuery.data?.categories?.nodes?.filter(isNotNullish)
                .map(category => [`/blog?c=${category.slug}`, <span style={{color: category?.fields?.color ?? ''}}>({category.fields?.initial}) {category.name}</span>] as const) ?? []
            ])}
            archiveMap={new Map([
              ...countByYearMonth(postsQuery.data?.posts?.nodes?.map(post => post?.date ? new Date(post.date) : null).filter(isNotNullish) ?? [])
            ])}
            urlGenerator={(time => `/blog?d=${toYearMonth(new Date(time))}`)}
          />
        </SideBarContainer>
        <MainContainer>
          <ItemContainer>
            {
              pagedPosts?.map(post => {
                return <PostListItem as="a" key={post.postId} href={`/blog/${post.postId}`}>
                  <ItemInner>
                    <ThumbnailOuter>
                      {
                        post.featuredImage?.node?.mediaItemUrl ?
                        <Thumbnail src={post.featuredImage?.node?.mediaItemUrl} width="320" height="160" /> :
                        <ThumbnailProxy>
                          <SvgLogo />
                        </ThumbnailProxy>
                      }
                    </ThumbnailOuter>
                    <BlogItemDetail
                      title={post.title ?? ''}
                      initial={post.categories?.nodes?.[0]?.fields?.initial ?? ''}
                      color={post.categories?.nodes?.[0]?.fields?.color ?? ''}
                      date={new Date(post.date ?? NaN)}
                      clip
                    />
                  </ItemInner>
                  <ArrowContainer>
                    <SvgRightTriangle />
                  </ArrowContainer>
                </PostListItem>
              })
            }
          </ItemContainer>
          <PaginatorContainer>
            <PostPaginator
              pageCount={Math.ceil((filteredPosts?.length ?? 0) / postInPage)}
              currentPage={currentPage}
              linkGenerator={page => `/blog?${targetMonth ? `d=${toYearMonth(targetMonth)}&` : ''}p=${page}`}
            />
          </PaginatorContainer>
        </MainContainer>
      </Inner>
    </ContentContainer>
  </Outer>
}